import { createPageTabNavigation } from "../page-tab-navigation.js";
import {
  calculatorFishingSourceSignalPatchV1 as fishingSourceSignalPatch,
} from "../components/calculator-fishing-source.js";
import { createCalculatorScenarioCommitter } from "../calculator-scenario-commit.js";
import {
  calculatorAcceptedFixedControlSignalPatchV1 as acceptedFixedControlSignalPatch,
} from "../components/calculator-fixed-control-signals.js";
import { DATASTAR_SIGNAL_PATCH_EVENT } from "../datastar-signals.js";
import { assertKnownPageSignalPatchRoots } from "../page-signal-patch-guard.js";
import {
  browserLootRateDisplayPreferenceRepository,
} from "../loot-rate-display-preferences.js";
import {
  createLootRateDisplayControlController,
} from "../loot-rate-display-controls.js";
import {
  DEFAULT_LOOT_RATE_DISPLAY_MODE,
  LOOT_RATE_DISPLAY_MODES,
} from "../loot-rate-display-preference.js";
import { browserPresetWorkspace } from "../presets/browser-workspace.js";
import {
  CALCULATOR_PRESET_SIGNAL_KEYS_V1,
  createCalculatorPresetPayload as createVersionedCalculatorPresetPayload,
  decodeCalculatorLayoutPresetPayload,
  decodeCalculatorPresetPayload,
  presetCollectionContract,
} from "../presets/collection-contracts.js";

(function () {
  const CALCULATOR_DATA_STORAGE_KEY = "fishystuff.calculator.data.v1";
  const CALCULATOR_UI_STORAGE_KEY = "fishystuff.calculator.ui.v1";
  const CALCULATOR_PERSIST_EXCLUDE_SIGNAL_PATTERN =
    /^_(?:loading|calculatorSourceReference|defaults|preset_manager_ui)(?:\.|$)/;
  const CALCULATOR_ACTION_SIGNAL_PATTERN = /^_calculator_actions(?:\.|$)/;
  const CALCULATOR_LAYOUT_UI_SIGNAL_PATTERN = /^_calculator_ui(?:\.|$)/;
  const CALCULATOR_PRESET_SIGNAL_FILTER = {
    include:
      /^(?:(?!_)[^.]+|_(?:outfit|food|buff)_slots|_pet[1-5]_skill_slot[1-3])(?:\.|$)/,
  };
  const CALCULATOR_CONTINUOUS_NUMERIC_SIGNAL_KEYS = new Set([
    "catchTimeActive",
    "catchTimeAfk",
    "mastery",
    "resources",
    "targetFishAmount",
    "targetFishPmfCount",
    "timespanAmount",
    "tradeDistanceBonus",
    "tradePriceCurve",
  ]);
  const CALCULATOR_DATA_SIGNAL_KEYS = new Set(CALCULATOR_PRESET_SIGNAL_KEYS_V1);
  const CALCULATOR_PRE_INIT_SIGNAL_ROOTS = new Set([
    ...CALCULATOR_PRESET_SIGNAL_KEYS_V1,
    "_buff_slots",
    "_food_slots",
    "_outfit_slots",
    ...Array.from(
      { length: 5 },
      (_unused, petIndex) => Array.from(
        { length: 3 },
        (_slot, skillIndex) => `_pet${petIndex + 1}_skill_slot${skillIndex + 1}`,
      ),
    ).flat(),
  ]);
  const CALCULATOR_SECTION_TABS = new Set([
    "mode",
    "overview",
    "zone",
    "bite_time",
    "catch_time",
    "session",
    "distribution",
    "loot",
    "trade",
    "gear",
    "food",
    "buffs",
    "pets",
    "layers",
  ]);
  const CALCULATOR_PRESET_COLLECTION_KEY = "calculator-presets";
  const CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY = "calculator-layouts";
  const CALCULATOR_DEFAULT_CUSTOM_SECTIONS = Object.freeze([
    "overview",
    "zone",
    "session",
    "bite_time",
    "loot",
  ]);
  const CALCULATOR_DEFAULT_CUSTOM_LAYOUT = Object.freeze([
    Object.freeze([Object.freeze(["overview"])]),
    Object.freeze([Object.freeze(["zone"]), Object.freeze(["session"])]),
    Object.freeze([Object.freeze(["bite_time"]), Object.freeze(["loot"])]),
  ]);
  const CALCULATOR_DISTRIBUTION_TABS = new Set([
    "groups",
    "value_split",
    "loot_flow",
    "target_fish",
  ]);
  const CALCULATOR_LOOT_METRICS = new Set([
    "silver",
    "exp",
    "totem_exp",
  ]);
  const CALCULATOR_LOOT_FLOW_SORT_BY_VALUES = new Set([
    "price",
    "grade",
    "droprate",
    "expected_value",
    "exp",
    "totem_exp",
    "alphabetical",
    "item_id",
    "database",
    "evidence",
  ]);
  const CALCULATOR_LOOT_FLOW_SORT_ORDER_VALUES = new Set(["desc", "asc"]);
  const CALCULATOR_ACTION_DEFAULTS = Object.freeze({
    copyUrlToken: 0,
    copyShareToken: 0,
  });
  const calculatorState = {
    persistBinding: null,
    actionBinding: null,
    layoutPresetBinding: null,
    calculatorPresetBinding: null,
    interactionBinding: null,
    lootRatePreferenceBinding: null,
    uiStateRestored: false,
    calculatorPresetRuntimeUnbind: null,
    calculatorPresetWorkspaceUnsubscribe: null,
    layoutPresetRuntimeUnbind: null,
    calculatorPresetEditQueue: Promise.resolve(),
    layoutPresetEditQueue: Promise.resolve(),
    calculatorPresetEditEpoch: 0,
    layoutPresetEditEpoch: 0,
    calculatorPresetApplyDepth: 0,
    layoutPresetApplyDepth: 0,
    calculatorPresetEditInitializationDepth: 0,
    calculatorPresetInitialization: null,
    detachedPresetPreview: false,
    pendingCalculatorPresetRestore: false,
    pendingLayoutPresetRestore: false,
    pendingCalculatorDataState: null,
    pendingCalculatorUiState: null,
    calculatorRecoveryRequest: null,
    calculatorRecoveryPromise: null,
    calculatorRecoveryBinding: null,
    calculatorCurrentAppliedBeforeInit: false,
    calculatorCurrentStructurallyUnreadable: false,
    projection: null,
    presentation: null,
  };
  const calculatorPetUiState = {
    imageFallbackBound: false,
  };

  const signalStore = window.__fishystuffDatastarState.createPageSignalStore();
  const calculatorTabs = createPageTabNavigation({
    page: "calculator",
    basePath: new URL(document.getElementById("page").dataset.pageTabBaseHref, window.location.href).pathname,
    root: document,
    project(tab) { patchCalculatorSignals({ _calculator_ui: { workspace_tab: tab } }); },
  });
  window.addEventListener("pagehide", () => calculatorTabs.stop());
  window.addEventListener("pageshow", () => {
    if (calculatorState.uiStateRestored) calculatorTabs.start();
  });
  const calculatorEngineController = window.FishyCalculatorEnginePage?.create?.({
    getSignals: () => signalStore.signalObject(),
    language: calculatorSurfaceLanguage,
    resolveApiUrl: (path) => window.__fishystuffResolveApiUrl(path),
    patchSignals: patchCalculatorSignals,
    navigateWorkspace: (tab) => calculatorTabs.navigate(tab),
    onProjection(projection, presentation) {
      calculatorState.projection = projection;
      calculatorState.presentation = presentation;
    },
  }) ?? null;
  const calculatorActionTokens =
    window.__fishystuffDatastarState.createCounterTokenController(
      CALCULATOR_ACTION_DEFAULTS,
    );
  const calculatorScenarioCommitter = createCalculatorScenarioCommitter({
    commit(signals) {
      trackCalculatorPresetCurrent(signals);
      persistCalculator(signals);
    },
    requestFrame: globalThis.requestAnimationFrame.bind(globalThis),
    cancelFrame: globalThis.cancelAnimationFrame.bind(globalThis),
    scheduleTask: globalThis.setTimeout.bind(globalThis),
    cancelTask: globalThis.clearTimeout.bind(globalThis),
    pageLifecycle: window,
  });

  function languageHelper() {
    const helper = window.__fishystuffLanguage;
    return helper && typeof helper.current === "function" && typeof helper.t === "function"
      ? helper
      : null;
  }


  function languageReady() {
    const ready = languageHelper()?.ready;
    return ready && typeof ready.then === "function" ? ready.catch(() => {}) : Promise.resolve();
  }

  const replacePetImageWithFallback = (image) => {
    if (!(image instanceof HTMLImageElement)) {
      return;
    }
    const frame = image.closest(".fishy-calculator-pet-option__frame");
    if (!(frame instanceof HTMLElement)) {
      return;
    }
    const fallback = document.createElement("span");
    fallback.className = "fishy-calculator-pet-option__fallback fishy-item-icon-fallback";
    fallback.textContent = String(
      image.dataset.fallbackLabel || image.getAttribute("data-fallback-label") || image.alt || "?",
    )
      .trim()
      .charAt(0)
      .toUpperCase() || "?";
    frame.replaceChildren(fallback);
  };

  function bindPetImageFallbackListener() {
    if (calculatorPetUiState.imageFallbackBound) {
      return;
    }
    document.addEventListener("error", (event) => {
      if (!(event.target instanceof HTMLImageElement)) {
        return;
      }
      if (!event.target.classList.contains("fishy-calculator-pet-option__image")) {
        return;
      }
      replacePetImageWithFallback(event.target);
    }, true);
    calculatorPetUiState.imageFallbackBound = true;
  }

  function calculatorSurfaceLanguage() {
    const current = languageHelper()?.current?.() || {};
    const locale = String(current.locale || document.documentElement.lang || "en-US").trim();
    const localeKey = locale.toLowerCase();
    const localeBase = localeKey.split(/[-_]/)[0];
    const apiLang = String(current.apiLang || "").trim().toLowerCase();
    const resolvedApiLang = apiLang || "en";
    if (localeBase === "ko") {
      return {
        locale: "ko-KR",
        apiLang: resolvedApiLang,
        lang: resolvedApiLang,
      };
    }
    if (localeBase === "de") {
      return {
        locale: "de-DE",
        apiLang: resolvedApiLang,
        lang: resolvedApiLang,
      };
    }
    return {
      locale: "en-US",
      apiLang: resolvedApiLang,
      lang: resolvedApiLang,
    };
  }

  function calculatorText(key, vars = {}, options = {}) {
    const helper = languageHelper();
    if (!helper) {
      return `calculator.${key}`;
    }
    return helper.t(`calculator.${key}`, vars, {
      locale: options.locale || calculatorSurfaceLanguage().locale,
    });
  }

  function presetWorkspace() {
    return browserPresetWorkspace();
  }

  function datastarPersistHelper() {
    const helper = window.__fishystuffDatastarPersist;
    return helper && typeof helper.createSignalPatchPersistor === "function"
      ? helper
      : null;
  }

  function lootRateDisplayMode(value) {
    return LOOT_RATE_DISPLAY_MODES.includes(value) ? value : null;
  }

  const isPlainObject = (value) => value && typeof value === "object" && !Array.isArray(value);

  function calculatorContinuousNumericPatch(patch) {
    if (!isPlainObject(patch)) {
      return false;
    }
    const keys = Object.keys(patch);
    return keys.length > 0
      && keys.every((key) => CALCULATOR_CONTINUOUS_NUMERIC_SIGNAL_KEYS.has(key));
  }

  function mergeCalculatorSignalPatch(target, patch) {
    if (!isPlainObject(target) || !isPlainObject(patch)) {
      return target;
    }
    for (const [key, value] of Object.entries(patch)) {
      if (isPlainObject(value) && isPlainObject(target[key])) {
        mergeCalculatorSignalPatch(target[key], value);
      } else {
        target[key] = cloneCalculatorSignals(value);
      }
    }
    return target;
  }

  function assignCalculatorSignalPatch(target, patch, options = {}) {
    if (!isPlainObject(target) || !isPlainObject(patch)) {
      return target;
    }
    if (options.replaceCalculatorData === true) {
      for (const key of Object.keys(target)) {
        if (!key.startsWith("_") && !(key in patch)) {
          delete target[key];
        }
      }
    }
    if (options.replaceTopLevel === true || options.replaceCalculatorData === true) {
      for (const [key, value] of Object.entries(patch)) {
        target[key] = cloneCalculatorSignals(value);
      }
      return target;
    }
    return mergeCalculatorSignalPatch(target, patch);
  }

  function bindPersistListener() {
    if (calculatorState.persistBinding) {
      return;
    }
    const helper = datastarPersistHelper();
    if (!helper) {
      return;
    }
    calculatorState.persistBinding = helper.createSignalPatchPersistor({
      isReady() {
        return calculatorState.uiStateRestored
          && !calculatorState.pendingCalculatorPresetRestore
          && !calculatorState.pendingLayoutPresetRestore
          && !calculatorState.pendingCalculatorDataState
          && !calculatorState.pendingCalculatorUiState;
      },
      filter: {
        exclude: CALCULATOR_PERSIST_EXCLUDE_SIGNAL_PATTERN,
      },
      shouldPersistPatch(patch) {
        return !calculatorContinuousNumericPatch(patch)
          && helper.patchMatchesSignalFilter(patch, {
            exclude: CALCULATOR_PERSIST_EXCLUDE_SIGNAL_PATTERN,
          });
      },
      persist() {
        const signals = signalStore.signalObject();
        if (!signals) {
          return;
        }
        persistCalculator(signals);
      },
    });
    calculatorState.persistBinding.bind();
  }

  function bindActionListener() {
    if (calculatorState.actionBinding) {
      return;
    }
    const helper = datastarPersistHelper();
    const patchMatches = helper && typeof helper.patchMatchesSignalFilter === "function"
      ? helper.patchMatchesSignalFilter
      : null;
    if (!patchMatches) {
      return;
    }
    const handleSignalPatch = (event) => {
      if (!calculatorState.uiStateRestored) {
        return;
      }
      const patch = event && event.detail ? event.detail : null;
      if (!patchMatches(patch, { include: CALCULATOR_ACTION_SIGNAL_PATTERN })) {
        return;
      }
      const signals = signalStore.signalObject();
      if (!signals) {
        return;
      }
      syncCalculatorActions(signals);
    };
    document.addEventListener(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
    calculatorState.actionBinding = {
      dispose() {
        document.removeEventListener?.(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
      },
    };
  }

  function bindLayoutPresetListener() {
    if (calculatorState.layoutPresetBinding) {
      return;
    }
    const helper = datastarPersistHelper();
    const patchMatches = helper && typeof helper.patchMatchesSignalFilter === "function"
      ? helper.patchMatchesSignalFilter
      : null;
    if (!patchMatches) {
      return;
    }
    const handleSignalPatch = (event) => {
      if (!calculatorState.uiStateRestored) {
        return;
      }
      const patch = event && event.detail ? event.detail : null;
      const signals = signalStore.signalObject();
      if (!signals) {
        return;
      }
      const layoutPatch = patchMatches(patch, { include: CALCULATOR_LAYOUT_UI_SIGNAL_PATTERN });
      if (!layoutPatch) {
        return;
      }
      trackCalculatorLayoutPresetCurrent(signals);
    };
    document.addEventListener(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
    calculatorState.layoutPresetBinding = {
      dispose() {
        document.removeEventListener?.(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
      },
    };
  }

  function bindCalculatorPresetListener() {
    if (calculatorState.calculatorPresetBinding) {
      return;
    }
    const helper = datastarPersistHelper();
    const patchMatches = helper && typeof helper.patchMatchesSignalFilter === "function"
      ? helper.patchMatchesSignalFilter
      : null;
    if (!patchMatches) {
      return;
    }
    const handleSignalPatch = (event) => {
      if (!calculatorState.uiStateRestored) {
        return;
      }
      const patch = event && event.detail ? event.detail : null;
      const signals = signalStore.signalObject();
      if (!signals) {
        return;
      }
      if (calculatorInitPatch(patch)) {
        if (calculatorState.calculatorRecoveryRequest) {
          void recoverCalculatorCurrentWhenReady(signals).catch(() => {});
          return;
        }
        void initializeCalculatorPresetWhenReady(signals);
        return;
      }
      if (isPlainObject(patch) && Object.hasOwn(patch, "_calculatorSourceReference")) {
        return;
      }
      if (calculatorContinuousNumericPatch(patch)) {
        return;
      }
      if (!patchMatches(patch, CALCULATOR_PRESET_SIGNAL_FILTER)) {
        return;
      }
      trackCalculatorPresetCurrent(signals);
    };
    document.addEventListener(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
    calculatorState.calculatorPresetBinding = {
      dispose() {
        document.removeEventListener?.(DATASTAR_SIGNAL_PATCH_EVENT, handleSignalPatch);
      },
    };
  }

  function bindCalculatorRecoveryListener() {
    if (calculatorState.calculatorRecoveryBinding) {
      return;
    }
    const handleRecovery = (event) => {
      const signals = signalStore.signalObject();
      if (!signals || calculatorState.detachedPresetPreview) {
        return;
      }
      if (isPlainObject(event?.detail?.normalizedScenario)) {
        calculatorState.calculatorPresetApplyDepth += 1;
        try {
          patchCalculatorSignals(
            acceptedFixedControlSignalPatch(event.detail.normalizedScenario),
            { replaceTopLevel: true },
          );
        } finally {
          calculatorState.calculatorPresetApplyDepth -= 1;
        }
      }
      const payload = calculatorPresetPayload(signals);
      const operation = enqueuePresetEdit(
        CALCULATOR_PRESET_COLLECTION_KEY,
        payload,
        "calculatorPresetEditQueue",
        "catalog-recovery",
      );
      calculatorState.calculatorRecoveryPromise = Promise.resolve(operation)
        .then((state) => {
          if (state?.kind !== "ready") {
            throw new Error(`Calculator catalog recovery reached ${state?.kind ?? "unknown"}.`);
          }
          persistCalculator(signals);
          return state;
        })
        .catch((error) => {
          showPresetActionError(error, "calculator.recovery.save_failed");
          throw error;
        })
        .finally(() => {
          calculatorState.calculatorRecoveryPromise = null;
        });
      void calculatorState.calculatorRecoveryPromise.catch(() => {});
    };
    document.addEventListener("fishystuff:calculator-catalog-recovered", handleRecovery);
    calculatorState.calculatorRecoveryBinding = {
      dispose() {
        document.removeEventListener?.(
          "fishystuff:calculator-catalog-recovered",
          handleRecovery,
        );
      },
    };
  }

  function bindCalculatorInteractionListener() {
    if (calculatorState.interactionBinding) {
      return;
    }
    const handleChange = (event) => {
      const input = event.target?.closest?.("input[data-pet-pack-leader]");
      if (input) {
        applyPackLeaderChange(input, input.getAttribute("data-pet-pack-leader-slot"));
      }
      const numericInput = event.target?.closest?.(
        'input[type="number"][data-bind], input[type="range"][data-bind]',
      );
      const signals = signalStore.signalObject();
      if (numericInput && signals && calculatorState.uiStateRestored) {
        calculatorScenarioCommitter.schedule(signals);
      }
    };
    const handleClick = (event) => {
      const control = event.target?.closest?.(
        "[data-calculator-custom-section-toggle], [data-calculator-custom-section-remove]",
      );
      if (!control) {
        return;
      }
      const stack = document.querySelector?.("fishy-calculator-section-stack");
      if (!stack) {
        return;
      }
      const toggleId = control.getAttribute("data-calculator-custom-section-toggle");
      if (toggleId) {
        stack.toggleCustomSection?.(toggleId);
        return;
      }
      const removeId = control.getAttribute("data-calculator-custom-section-remove");
      if (removeId) {
        blurActiveElement();
        stack.removeCustomSection?.(removeId);
      }
    };
    document.addEventListener("change", handleChange);
    document.addEventListener("click", handleClick);
    calculatorState.interactionBinding = {
      dispose() {
        document.removeEventListener?.("change", handleChange);
        document.removeEventListener?.("click", handleClick);
      },
    };
  }

  function projectLootRateDisplayMode(value, signals = signalStore.signalObject()) {
    if (!isPlainObject(signals)) {
      return null;
    }
    const mode = lootRateDisplayMode(value) ?? DEFAULT_LOOT_RATE_DISPLAY_MODE;
    const currentUi = isPlainObject(signals._calculator_ui)
      ? signals._calculator_ui
      : null;
    if (currentUi?.loot_rate_display_mode === mode) {
      return mode;
    }
    const patch = {
      _calculator_ui: {
        loot_rate_display_mode: mode,
      },
    };
    assertKnownPageSignalPatchRoots("calculator", signals, patch);
    if (currentUi) {
      currentUi.loot_rate_display_mode = mode;
    } else {
      signals._calculator_ui = patch._calculator_ui;
    }
    return mode;
  }

  function bindLootRateDisplayPreference() {
    if (calculatorState.lootRatePreferenceBinding) {
      return;
    }
    const controller = createLootRateDisplayControlController({
      root: document,
      repository: browserLootRateDisplayPreferenceRepository(),
      projectMode(mode) {
        if (calculatorState.uiStateRestored) {
          projectLootRateDisplayMode(mode);
        }
      },
    });
    controller.start();
    const handlePageHide = () => calculatorState.lootRatePreferenceBinding?.dispose();
    calculatorState.lootRatePreferenceBinding = {
      dispose() {
        controller.stop();
        window.removeEventListener?.("pagehide", handlePageHide);
        calculatorState.lootRatePreferenceBinding = null;
      },
    };
    window.addEventListener("pagehide", handlePageHide, { once: true });
  }

  const urlParams = new URLSearchParams(window.location.search);
  const INVALID_DOLT_REF_CHARACTER = /[^A-Za-z0-9._\/^~-]/;
  const calculatorPreviewRef = (() => {
    const value = urlParams.get("ref");
    return typeof value === "string"
      && value !== ""
      && !INVALID_DOLT_REF_CHARACTER.test(value)
      ? value
      : "";
  })();
  const presetQueryParam = urlParams.get("preset");
  let sharedCalculatorPresetPayload = null;

  function removeCalculatorPresetQueryParam() {
    if (!presetQueryParam) {
      return;
    }
    const nextParams = new URLSearchParams(window.location.search);
    nextParams.delete("preset");
    const newQueryString = nextParams.toString();
    const newUrl =
      window.location.origin
      + window.location.pathname
      + (newQueryString ? "?" + newQueryString : "");
    try {
      window.history?.replaceState?.(window.history.state ?? null, "", newUrl);
    } catch (_error) {
      // Leaving the URL untouched is safer than reloading and losing the in-memory payload.
    }
  }

  if (presetQueryParam) {
    try {
      const jsonString = LZString.decompressFromEncodedURIComponent(presetQueryParam);
      const parsed = JSON.parse(jsonString);
      if (!isPlainObject(parsed)) {
        throw new Error("Shared calculator preset payload must be an object.");
      }
      sharedCalculatorPresetPayload = parsed;
      removeCalculatorPresetQueryParam();
    } catch (error) {
      console.error("Error importing preset:", error);
    }
  }

  const loadStoredJson = (storageKey, label) => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
      console.error(`Error parsing stored ${label}:`, error);
      return null;
    }
  };

  const loadStoredSignals = (options = {}) => {
    const storedData = options.includeData === false
      ? null
      : loadStoredJson(CALCULATOR_DATA_STORAGE_KEY, "calculator data");
    const storedUi = loadStoredJson(CALCULATOR_UI_STORAGE_KEY, "calculator UI state");
    if (!storedData && !storedUi) {
      return {
        signals: null,
        hasStoredData: false,
        hasStoredUi: false,
      };
    }
    const combined = storedData && typeof storedData === "object" ? { ...storedData } : {};
    if (storedUi && typeof storedUi === "object") {
      combined._calculator_ui = storedUi;
    }
    return {
      signals: combined,
      hasStoredData: Boolean(storedData),
      hasStoredUi: Boolean(storedUi),
    };
  };

  const compactStringArray = (value) => {
    if (!Array.isArray(value)) {
      return [];
    }
    const seen = new Set();
    const out = [];
    for (const entry of value) {
      const normalized = String(entry ?? "").trim();
      if (!normalized || seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      out.push(normalized);
    }
    return out;
  };

  const canonicalizePetSignals = (value) => {
    const current = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const kind = current.kind === "custom" ? "custom" : "catalog";
    const basedOn = kind === "custom"
      ? String(current.basedOn ?? current.pet ?? "").trim()
      : "";
    return {
      ...current,
      kind,
      displayLabel: kind === "custom" && typeof current.displayLabel === "string"
        ? (current.displayLabel.trim() || null)
        : null,
      basedOn,
      pet: kind === "custom" ? basedOn : String(current.pet ?? "").trim(),
      packLeader: current.packLeader === true,
      skills: compactStringArray(current.skills),
    };
  };

  const normalizeCustomSections = (
    value,
    fallback = CALCULATOR_DEFAULT_CUSTOM_SECTIONS,
  ) => {
    const normalizeList = (entries) => compactStringArray(entries)
      .filter((entry) => CALCULATOR_SECTION_TABS.has(entry));
    if (Array.isArray(value)) {
      return normalizeList(value);
    }
    return normalizeList(fallback);
  };

  const flattenCustomLayout = (value) => {
    const seen = new Set();
    const rows = Array.isArray(value) ? value : [];
    const out = [];
    for (const row of rows) {
      if (!Array.isArray(row)) {
        continue;
      }
      for (const column of row) {
        if (!Array.isArray(column)) {
          continue;
        }
        for (const entry of column) {
          const normalized = String(entry ?? "").trim();
          if (!normalized || !CALCULATOR_SECTION_TABS.has(normalized) || seen.has(normalized)) {
            continue;
          }
          seen.add(normalized);
          out.push(normalized);
        }
      }
    }
    return out;
  };

  const normalizeCustomLayout = (
    value,
    fallback = CALCULATOR_DEFAULT_CUSTOM_LAYOUT,
  ) => {
    const fallbackRows = Array.isArray(fallback?.[0]?.[0])
      ? fallback
      : normalizeCustomSections(fallback).map((sectionId) => [[sectionId]]);
    const rows = Array.isArray(value) ? value : fallbackRows;
    const seen = new Set();
    const out = [];
    for (const row of rows) {
      if (!Array.isArray(row)) {
        continue;
      }
      const normalizedRow = [];
      for (const column of row) {
        if (!Array.isArray(column)) {
          continue;
        }
        const normalizedColumn = [];
        for (const entry of column) {
          const normalized = String(entry ?? "").trim();
          if (!normalized || !CALCULATOR_SECTION_TABS.has(normalized) || seen.has(normalized)) {
            continue;
          }
          seen.add(normalized);
          normalizedColumn.push(normalized);
        }
        if (normalizedColumn.length) {
          normalizedRow.push(normalizedColumn);
        }
      }
      if (normalizedRow.length) {
        out.push(normalizedRow);
      }
    }
    if (Array.isArray(value)) {
      return out;
    }
    if (out.length) {
      return out;
    }
    const normalizedFallback = normalizeCustomSections(fallbackRows.flat(2));
    return normalizedFallback.length
      ? normalizedFallback.map((sectionId) => [[sectionId]])
      : normalizeCustomSections(CALCULATOR_DEFAULT_CUSTOM_SECTIONS)
        .map((sectionId) => [[sectionId]]);
  };

  const customLayoutFromUiState = (value) => {
    if (Array.isArray(value)) {
      return Array.isArray(value[0]?.[0])
        ? normalizeCustomLayout(value)
        : normalizeCustomLayout(undefined, value);
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return normalizeCustomLayout(undefined);
    }
    if (Object.prototype.hasOwnProperty.call(value, "custom_layout")) {
      return normalizeCustomLayout(value.custom_layout);
    }
    return normalizeCustomLayout(undefined);
  };

  const uiStateWithCustomLayout = (value, layout) => {
    const current = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const customLayout = normalizeCustomLayout(layout);
    return {
      ...current,
      custom_layout: customLayout,
      custom_sections: flattenCustomLayout(customLayout),
    };
  };

  const normalizeCalculatorUiState = (value) => {
    const current = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const distributionTab = String(
      current.distribution_tab || "groups",
    ).trim();
    let normalizedDistributionTab = distributionTab;
    let valueSplitMetric = String(
      current.value_split_metric || "silver",
    ).trim();
    let lootFlowMetric = String(
      current.loot_flow_metric || "silver",
    ).trim();
    if (["silver", "exp", "totem_exp"].includes(distributionTab)) {
      normalizedDistributionTab = "value_split";
      valueSplitMetric = distributionTab;
    } else if (distributionTab === "exp_flow") {
      normalizedDistributionTab = "loot_flow";
      lootFlowMetric = "exp";
    } else if (distributionTab === "totem_exp_flow") {
      normalizedDistributionTab = "loot_flow";
      lootFlowMetric = "totem_exp";
    }
    if (!CALCULATOR_LOOT_METRICS.has(valueSplitMetric)) {
      valueSplitMetric = "silver";
    }
    if (!CALCULATOR_LOOT_METRICS.has(lootFlowMetric)) {
      lootFlowMetric = "silver";
    }
    const lootFlowSortBy = String(current.loot_flow_sort_by ?? "")
      .trim()
      .toLowerCase();
    const lootFlowSortOrder = String(current.loot_flow_sort_order ?? "")
      .trim()
      .toLowerCase();
    const customLayout = customLayoutFromUiState(current);
    const normalized = {
      workspace_tab: calculatorTabs.selected(),
      distribution_tab: CALCULATOR_DISTRIBUTION_TABS.has(normalizedDistributionTab)
        ? normalizedDistributionTab
        : "groups",
      value_split_metric: valueSplitMetric,
      loot_flow_metric: lootFlowMetric,
      loot_flow_sort_by: CALCULATOR_LOOT_FLOW_SORT_BY_VALUES.has(lootFlowSortBy)
        ? lootFlowSortBy
        : "price",
      loot_flow_sort_order: CALCULATOR_LOOT_FLOW_SORT_ORDER_VALUES.has(lootFlowSortOrder)
        ? lootFlowSortOrder
        : "desc",
      show_inactive_zones: current.show_inactive_zones === true,
      custom_layout: customLayout,
      custom_sections: flattenCustomLayout(customLayout),
    };
    const mode = lootRateDisplayMode(current.loot_rate_display_mode);
    if (mode !== null) {
      normalized.loot_rate_display_mode = mode;
    }
    return normalized;
  };

  const cloneCalculatorSignals = (value) => JSON.parse(JSON.stringify(value));

  function assignCustomUiState(targetUiState, nextUiState) {
    if (!targetUiState || typeof targetUiState !== "object" || Array.isArray(targetUiState)) {
      return normalizeCalculatorUiState(nextUiState);
    }
    const normalized = normalizeCalculatorUiState({
      ...targetUiState,
      ...(nextUiState && typeof nextUiState === "object" && !Array.isArray(nextUiState)
        ? nextUiState
        : {}),
    });
    targetUiState.workspace_tab = normalized.workspace_tab;
    targetUiState.distribution_tab = normalized.distribution_tab;
    targetUiState.value_split_metric = normalized.value_split_metric;
    targetUiState.loot_flow_metric = normalized.loot_flow_metric;
    targetUiState.loot_flow_sort_by = normalized.loot_flow_sort_by;
    targetUiState.loot_flow_sort_order = normalized.loot_flow_sort_order;
    targetUiState.show_inactive_zones = normalized.show_inactive_zones;
    targetUiState.custom_layout = normalized.custom_layout;
    targetUiState.custom_sections = normalized.custom_sections;
    return targetUiState;
  }

  function calculatorLayoutPresetPayload(uiState) {
    return decodeCalculatorLayoutPresetPayload({
      version: 1,
      customLayout: customLayoutFromUiState(uiState),
    });
  }

  function applyCalculatorLayoutPreset(uiState, payload) {
    const current = normalizeCalculatorUiState(uiState);
    const layoutPreset = decodeCalculatorLayoutPresetPayload(payload);
    return assignCustomUiState(current, {
      custom_layout: layoutPreset.customLayout,
      custom_sections: flattenCustomLayout(layoutPreset.customLayout),
    });
  }

  function defaultCalculatorLayoutPresetPayload() {
    return calculatorLayoutPresetPayload({
      custom_layout: CALCULATOR_DEFAULT_CUSTOM_LAYOUT,
    });
  }

  function patchCalculatorSignals(patch, options = {}) {
    if (!isPlainObject(patch)) {
      return null;
    }
    const signals = signalStore.signalObject();
    assertKnownPageSignalPatchRoots("calculator", signals, patch, {
      permittedRoots: CALCULATOR_PRE_INIT_SIGNAL_ROOTS,
    });
    const normalizedPatch = cloneCalculatorSignals(patch);
    const eventPatch = cloneCalculatorSignals(normalizedPatch);
    if (options.replaceCalculatorData === true && isPlainObject(signals)) {
      for (const key of Object.keys(signals)) {
        if (!key.startsWith("_") && !(key in normalizedPatch)) {
          eventPatch[key] = null;
        }
      }
    }
    const applyPatch = () => assignCalculatorSignalPatch(signals, normalizedPatch, {
      replaceCalculatorData: options.replaceCalculatorData === true,
      replaceTopLevel: options.replaceTopLevel === true,
    });
    applyPatch();
    dispatchCalculatorSignalPatch(eventPatch);
    return signalStore.signalObject();
  }

  function dispatchCalculatorSignalPatch(patch) {
    if (!isPlainObject(patch) || Object.keys(patch).length === 0) {
      return;
    }
    const detail = cloneCalculatorSignals(patch);
    document.dispatchEvent?.(
      new CustomEvent(DATASTAR_SIGNAL_PATCH_EVENT, {
        detail,
      }),
    );
  }

  function prepareTradeDestinationPresetPresentation(signals, patch) {
    const currentDestination = String(signals?.tradeDestinationNpc ?? "");
    const currentOrigin = String(signals?.tradeOriginRegion ?? "");
    const targetDestination = String(patch?.tradeDestinationNpc ?? "");
    const targetOrigin = String(patch?.tradeOriginRegion ?? "");
    if (
      currentDestination === targetDestination
      && currentOrigin === targetOrigin
    ) {
      return;
    }

    const picker = document.getElementById?.("calculator-trade-destination-picker");
    if (typeof picker?.prepareValuePresentation !== "function") {
      return;
    }
    const pickerSearchUrl = String(picker.searchUrl ?? "").trim();
    if (!pickerSearchUrl) {
      throw new Error("Trade destination picker has no presentation source.");
    }
    const resolvedSearchUrl = picker.searchUrlRoot === "api"
      ? window.__fishystuffResolveApiUrl(pickerSearchUrl)
      : pickerSearchUrl;
    const searchUrl = new URL(resolvedSearchUrl, window.location.href);
    searchUrl.searchParams.set("trade_origin", targetOrigin);
    return picker.prepareValuePresentation(targetDestination, {
      searchUrl: searchUrl.toString(),
    });
  }

  function replaceCalculatorPresetSignals(signals, payload) {
    if (!signals || typeof signals !== "object") {
      return null;
    }
    const patch = calculatorPresetPatch(signals, payload);
    const applyPatch = () => {
      const engine = document.getElementById?.("calculator-engine");
      const transactional = typeof engine?.beginScenarioIntentTransaction === "function"
        && typeof engine?.commitScenarioIntentTransaction === "function"
        && typeof engine?.cancelScenarioIntentTransaction === "function";
      if (transactional) {
        engine.beginScenarioIntentTransaction();
      }
      try {
        patchCalculatorSignals(patch, {
          replaceCalculatorData: true,
        });
        if (transactional) {
          engine.commitScenarioIntentTransaction();
        }
      } catch (error) {
        if (transactional) {
          engine.cancelScenarioIntentTransaction();
        }
        throw error;
      }
      return calculatorPresetPayload({
        ...signals,
        ...patch,
      });
    };
    const presentation = prepareTradeDestinationPresetPresentation(signals, patch);
    return presentation && typeof presentation.then === "function"
      ? Promise.resolve(presentation).then(applyPatch)
      : applyPatch();
  }

  function replaceCalculatorUiSignals(signals, nextUiState) {
    if (!signals || typeof signals !== "object") {
      return null;
    }
    const patch = {
      _calculator_ui: cloneCalculatorSignals(nextUiState),
    };
    patchCalculatorSignals(patch, {
      replaceTopLevel: true,
    });
    return signals._calculator_ui;
  }

  function presetActionId(prefix) {
    const randomId = globalThis.crypto?.randomUUID?.();
    return randomId
      ? `${prefix}_${randomId}`
      : `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  }

  function bindCalculatorPresetRuntimes() {
    const workspace = presetWorkspace();
    if (!calculatorState.calculatorPresetRuntimeUnbind) {
      calculatorState.calculatorPresetRuntimeUnbind = workspace.bindCollectionRuntime(
        CALCULATOR_PRESET_COLLECTION_KEY,
        {
          captureCurrent() {
            const signals = signalStore.signalObject();
            return signals && calculatorPresetDefaultsReady(signals)
              ? calculatorPresetPayload(signals)
              : undefined;
          },
          applyCurrent(payload, context = {}) {
            const signals = signalStore.signalObject();
            if (!signals) {
              return null;
            }
            if (calculatorState.detachedPresetPreview && context.cause === "load") {
              return null;
            }
            if (
              context.cause === "load"
              && calculatorState.calculatorPresetEditInitializationDepth > 0
            ) {
              return null;
            }
            calculatorState.calculatorPresetEditEpoch += 1;
            calculatorState.calculatorPresetApplyDepth += 1;
            try {
              calculatorState.detachedPresetPreview = false;
              const replacement = replaceCalculatorPresetSignals(signals, payload);
              if (replacement && typeof replacement.then === "function") {
                return Promise.resolve(replacement).finally(() => {
                  calculatorState.calculatorPresetApplyDepth -= 1;
                });
              }
              calculatorState.calculatorPresetApplyDepth -= 1;
              return replacement;
            } catch (error) {
              calculatorState.calculatorPresetApplyDepth -= 1;
              throw error;
            }
          },
        },
      );
    }
    if (!calculatorState.calculatorPresetWorkspaceUnsubscribe) {
      const contract = presetCollectionContract(CALCULATOR_PRESET_COLLECTION_KEY);
      calculatorState.calculatorPresetWorkspaceUnsubscribe = workspace.subscribe(
        CALCULATOR_PRESET_COLLECTION_KEY,
        (state) => {
          const signals = signalStore.signalObject();
          if (
            !calculatorState.detachedPresetPreview
            || state.kind !== "ready"
            || !signals
            || !calculatorPresetDefaultsReady(signals)
          ) {
            return;
          }
          if (contract.payloadsEqual(state.current.payload, calculatorPresetPayload(signals))) {
            calculatorState.detachedPresetPreview = false;
          }
        },
      );
    }
    if (!calculatorState.layoutPresetRuntimeUnbind) {
      calculatorState.layoutPresetRuntimeUnbind = workspace.bindCollectionRuntime(
        CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY,
        {
          captureCurrent() {
            const signals = signalStore.signalObject();
            return signals
              ? calculatorLayoutPresetPayload(signals._calculator_ui)
              : undefined;
          },
          applyCurrent(payload) {
            const signals = signalStore.signalObject();
            if (!signals) {
              return null;
            }
            calculatorState.layoutPresetEditEpoch += 1;
            calculatorState.layoutPresetApplyDepth += 1;
            try {
              return replaceCalculatorUiSignals(
                signals,
                applyCalculatorLayoutPreset(signals._calculator_ui, payload),
              );
            } finally {
              calculatorState.layoutPresetApplyDepth -= 1;
            }
          },
        },
      );
    }
  }

  function presetEditEpoch(collectionKey) {
    if (collectionKey === CALCULATOR_PRESET_COLLECTION_KEY) {
      return calculatorState.calculatorPresetEditEpoch;
    }
    if (collectionKey === CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY) {
      return calculatorState.layoutPresetEditEpoch;
    }
    throw new TypeError(`Unknown calculator preset collection ${collectionKey}`);
  }

  function presetWorkspaceNeedsLoad(state) {
    return state?.kind === "uninitialized" || state?.kind === "unavailable";
  }

  function enqueuePresetEdit(collectionKey, payload, queueField, actionKind) {
    const workspace = presetWorkspace();
    const actionId = presetActionId(actionKind.replaceAll("-", "_"));
    const editEpoch = presetEditEpoch(collectionKey);
    const operation = calculatorState[queueField]
      .catch(() => null)
      .then(async () => {
        if (editEpoch !== presetEditEpoch(collectionKey)) {
          return workspace.state(collectionKey);
        }
        let current = workspace.state(collectionKey);
        if (presetWorkspaceNeedsLoad(current)) {
          current = await workspace.load(collectionKey);
          if (editEpoch !== presetEditEpoch(collectionKey)) {
            return workspace.state(collectionKey);
          }
        }
        if (
          collectionKey === CALCULATOR_PRESET_COLLECTION_KEY
          && current.kind === "missing"
        ) {
          const signals = signalStore.signalObject();
          if (!calculatorPresetDefaultsReady(signals)) {
            return current;
          }
          calculatorState.calculatorPresetEditInitializationDepth += 1;
          try {
            await initializeCalculatorPresetWhenReady(signals);
          } finally {
            calculatorState.calculatorPresetEditInitializationDepth -= 1;
          }
          if (editEpoch !== presetEditEpoch(collectionKey)) {
            return workspace.state(collectionKey);
          }
          current = workspace.state(collectionKey);
          if (current.kind === "missing") {
            return current;
          }
        }
        if (current.kind === "missing") {
          return workspace.initialize(collectionKey, payload, {
            expectedRevision: current.revision,
            source: { kind: "none", id: "" },
          });
        }
        if (current.kind !== "ready") {
          return current;
        }
        return workspace.edit(collectionKey, payload, {
          expectedRevision: current.revision,
          actionId,
          actionKind,
        });
      });
    calculatorState[queueField] = operation.catch((error) => {
      return null;
    });
    return operation;
  }

  function trackCalculatorLayoutPresetCurrent(signals) {
    if (calculatorState.layoutPresetApplyDepth > 0) {
      return null;
    }
    const payload = calculatorLayoutPresetPayload(signals?._calculator_ui);
    void enqueuePresetEdit(
      CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY,
      payload,
      "layoutPresetEditQueue",
      "layout-edit",
    );
    return payload;
  }

  function trackCalculatorPresetCurrent(signals) {
    if (
      calculatorState.calculatorPresetApplyDepth > 0
      || calculatorState.detachedPresetPreview
      || !calculatorPresetDefaultsReady(signals)
    ) {
      return null;
    }
    const payload = calculatorPresetPayload(signals);
    void enqueuePresetEdit(
      CALCULATOR_PRESET_COLLECTION_KEY,
      payload,
      "calculatorPresetEditQueue",
      "calculator-edit",
    );
    return payload;
  }

  function presetFallbackText(template, vars = {}) {
    return String(template ?? "").replace(/\{\s*\$([A-Za-z0-9_]+)\s*\}/g, (_match, name) => (
      Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : ""
    ));
  }

  function presetText(key, vars = {}, fallback = "") {
    const helper = languageHelper();
    const normalizedKey = String(key ?? "").trim();
    if (!helper || !normalizedKey) {
      return presetFallbackText(fallback || normalizedKey, vars);
    }
    const translated = helper.t(normalizedKey, vars, {
      locale: calculatorSurfaceLanguage().locale,
    });
    return !translated || translated === normalizedKey
      ? presetFallbackText(fallback || normalizedKey, vars)
      : translated;
  }

  function showPresetActionError(_error, fallbackKey) {
    const message = presetText(fallbackKey);
    const toast = window.__fishystuffToast;
    if (typeof toast?.error === "function") {
      toast.error(message);
      return;
    }
    toast?.info?.(message);
  }

  function calculatorInitPatch(patch) {
    return Boolean(
      patch
      && typeof patch === "object"
      && (
        Object.prototype.hasOwnProperty.call(patch, "_defaults")
        || Object.prototype.hasOwnProperty.call(patch, "_loading")
      ),
    );
  }

  const canonicalizeStoredSignals = (signals) => {
    const source = signals && typeof signals === "object" && !Array.isArray(signals)
      ? signals
      : {};
    const current = Object.fromEntries(
      Array.from(CALCULATOR_DATA_SIGNAL_KEYS)
        .filter((key) => Object.prototype.hasOwnProperty.call(source, key))
        .map((key) => [key, source[key]]),
    );
    current._calculator_ui = normalizeCalculatorUiState(source._calculator_ui);
    current.outfit = compactStringArray(current.outfit);
    current.food = compactStringArray(current.food);
    current.buff = compactStringArray(current.buff);
    let packLeaderSeen = false;
    for (const petKey of ["pet1", "pet2", "pet3", "pet4", "pet5"]) {
      if (!current[petKey] || typeof current[petKey] !== "object" || Array.isArray(current[petKey])) {
        continue;
      }
      const normalizedPet = canonicalizePetSignals(current[petKey]);
      normalizedPet.packLeader = normalizedPet.packLeader && !packLeaderSeen;
      packLeaderSeen ||= normalizedPet.packLeader;
      current[petKey] = normalizedPet;
      for (let index = 0; index < 3; index += 1) {
        current[`_${petKey}_skill_slot${index + 1}`] = String(normalizedPet.skills?.[index] ?? "");
      }
    }
    return current;
  };

  const persistedCalculatorSignals = (signals) => {
    const current = canonicalizeStoredSignals(signals);
    return Object.fromEntries(
      Object.entries(current).filter(
        ([key]) => !key.startsWith("_"),
      ),
    );
  };

  function calculatorPresetBaseSignals(signals = signalStore.signalObject()) {
    return signals?._defaults && typeof signals._defaults === "object"
      ? persistedCalculatorSignals(signals._defaults)
      : {};
  }

  function calculatorPresetDefaultsReady(signals = signalStore.signalObject()) {
    return Object.keys(calculatorPresetBaseSignals(signals)).length > 0;
  }

  function normalizeCalculatorPresetPayload(value) {
    return decodeCalculatorPresetPayload(value);
  }

  function calculatorPresetPayload(signals = signalStore.signalObject()) {
    return createVersionedCalculatorPresetPayload({
      ...calculatorPresetBaseSignals(signals),
      ...persistedCalculatorSignals(signals),
    });
  }

  function defaultCalculatorPresetSignals(signals) {
    return signals?._defaults && typeof signals._defaults === "object"
      ? persistedCalculatorSignals(signals._defaults)
      : calculatorPresetBaseSignals(signals);
  }

  function calculatorControlSignalPatch(payload) {
    const patch = cloneCalculatorSignals(payload && typeof payload === "object" ? payload : {});
    if ("outfit" in patch) {
      patch._outfit_slots = compactStringArray(patch.outfit);
    }
    if ("food" in patch) {
      patch._food_slots = compactStringArray(patch.food);
    }
    if ("buff" in patch) {
      patch._buff_slots = compactStringArray(patch.buff);
    }
    for (const petKey of ["pet1", "pet2", "pet3", "pet4", "pet5"]) {
      const pet = patch[petKey];
      if (!pet || typeof pet !== "object" || Array.isArray(pet)) {
        continue;
      }
      const skills = compactStringArray(pet.skills);
      for (let index = 0; index < 3; index += 1) {
        patch[`_${petKey}_skill_slot${index + 1}`] = String(skills[index] ?? "");
      }
    }
    return patch;
  }

  function calculatorPresetPatch(signals, payload) {
    const decoded = normalizeCalculatorPresetPayload(payload);
    return calculatorControlSignalPatch({
      ...defaultCalculatorPresetSignals(signals),
      ...decoded.signals,
    });
  }

  function initializeCalculatorPresetWhenReady(signals) {
    if (!calculatorPresetDefaultsReady(signals)) {
      return Promise.resolve(presetWorkspace().state(CALCULATOR_PRESET_COLLECTION_KEY));
    }
    if (calculatorState.calculatorPresetInitialization) {
      return calculatorState.calculatorPresetInitialization;
    }
    const defaultPayload = createVersionedCalculatorPresetPayload(
      defaultCalculatorPresetSignals(signals),
    );
    calculatorState.calculatorPresetInitialization = (async () => {
      const workspace = presetWorkspace();
      await workspace.defineFixedPreset(CALCULATOR_PRESET_COLLECTION_KEY, {
        id: "default",
        name: "Default",
        payload: defaultPayload,
      });
      let current = workspace.state(CALCULATOR_PRESET_COLLECTION_KEY);
      if (presetWorkspaceNeedsLoad(current)) {
        current = await workspace.load(CALCULATOR_PRESET_COLLECTION_KEY);
      }
      if (current.kind === "missing") {
        current = await workspace.initialize(
          CALCULATOR_PRESET_COLLECTION_KEY,
          defaultPayload,
          {
            expectedRevision: current.revision,
            source: { kind: "fixed", id: "default" },
          },
        );
      } else if (
        current.kind === "ready"
        && !calculatorState.detachedPresetPreview
        && !calculatorState.calculatorCurrentAppliedBeforeInit
      ) {
        await replaceCalculatorPresetSignals(signals, current.current.payload);
      }
      calculatorState.pendingCalculatorPresetRestore = current.kind === "unavailable";
      return current;
    })().finally(() => {
      calculatorState.calculatorPresetInitialization = null;
    });
    return calculatorState.calculatorPresetInitialization;
  }

  const persistedCalculatorUiSignals = (signals) => {
    const current = canonicalizeStoredSignals(signals);
    const persisted = cloneCalculatorSignals(current._calculator_ui);
    delete persisted.workspace_tab;
    delete persisted.loot_rate_display_mode;
    delete persisted.initialization_issue;
    return persisted;
  };

  const sharedCalculatorSignals = (signals) =>
    Object.fromEntries(
      Object.entries(canonicalizeStoredSignals(signals)).filter(
        ([key]) => !key.startsWith("_"),
      ),
    );

  const presetURL = (signals) => {
    const payload = JSON.stringify(sharedCalculatorSignals(signals));
    return (
      window.location.origin
      + window.location.pathname
      + "?preset="
      + LZString.compressToEncodedURIComponent(payload)
      + (calculatorPreviewRef ? `&ref=${encodeURIComponent(calculatorPreviewRef)}` : "")
    );
  };

  function calculatorInitUrl({ recovery = false } = {}) {
    const language = calculatorSurfaceLanguage();
    return window.__fishystuffResolveApiUrl(
      `/api/v1/calculator/datastar/init?lang=${language.apiLang}&locale=${encodeURIComponent(language.locale)}`
        + (calculatorPreviewRef ? `&ref=${encodeURIComponent(calculatorPreviewRef)}` : "")
        + (recovery ? "&recovery=current-defaults" : ""),
    );
  }

  function retryCalculatorInitialization() {
    calculatorState.calculatorRecoveryRequest = null;
    return calculatorInitUrl();
  }

  async function beginCalculatorRecovery() {
    calculatorState.calculatorPresetEditEpoch += 1;
    const detachedPreview = calculatorState.detachedPresetPreview;
    try {
      await calculatorState.calculatorPresetEditQueue.catch(() => null);
      if (!detachedPreview) {
        const workspace = presetWorkspace();
        const cleared = await workspace.clearCurrent(CALCULATOR_PRESET_COLLECTION_KEY, {
          resetInvalid: true,
        });
        if (cleared?.kind !== "missing") {
          throw new Error(`Calculator Current reset reached ${cleared?.kind ?? "unknown"}.`);
        }
        localStorage.removeItem(CALCULATOR_DATA_STORAGE_KEY);
        calculatorState.calculatorCurrentAppliedBeforeInit = false;
      }
      calculatorState.calculatorRecoveryRequest = Object.freeze({ detachedPreview });
      calculatorState.calculatorCurrentStructurallyUnreadable = false;
      const signals = signalStore.signalObject();
      if (isPlainObject(signals?._calculator_ui)) {
        signals._calculator_ui.initialization_issue = "";
      }
      return calculatorInitUrl({ recovery: true });
    } catch (error) {
      showPresetActionError(error, "calculator.recovery.save_failed");
      const signals = signalStore.signalObject();
      if (isPlainObject(signals?._calculator_ui)) {
        signals._calculator_ui.initialization_issue = "unreadable-current";
        signals._calculator_ui.init_finished = true;
      }
      return null;
    }
  }

  function recoverCalculatorCurrentWhenReady(signals) {
    if (calculatorState.calculatorRecoveryPromise) {
      return calculatorState.calculatorRecoveryPromise;
    }
    const request = calculatorState.calculatorRecoveryRequest;
    if (!request || !calculatorPresetDefaultsReady(signals)) {
      return Promise.resolve(presetWorkspace().state(CALCULATOR_PRESET_COLLECTION_KEY));
    }
    const defaultPayload = createVersionedCalculatorPresetPayload(
      defaultCalculatorPresetSignals(signals),
    );
    const operation = (async () => {
      await calculatorState.calculatorPresetEditQueue.catch(() => null);
      if (request.detachedPreview) {
        calculatorState.detachedPresetPreview = true;
        calculatorState.pendingCalculatorPresetRestore = false;
        calculatorState.calculatorRecoveryRequest = null;
        return presetWorkspace().state(CALCULATOR_PRESET_COLLECTION_KEY);
      }
      calculatorState.detachedPresetPreview = false;
      const workspace = presetWorkspace();
      await workspace.defineFixedPreset(CALCULATOR_PRESET_COLLECTION_KEY, {
        id: "default",
        name: "Default",
        payload: defaultPayload,
      });
      let current = workspace.state(CALCULATOR_PRESET_COLLECTION_KEY);
      if (presetWorkspaceNeedsLoad(current)) {
        current = await workspace.load(CALCULATOR_PRESET_COLLECTION_KEY);
      }
      if (current.kind === "missing") {
        current = await workspace.initialize(
          CALCULATOR_PRESET_COLLECTION_KEY,
          defaultPayload,
          {
            expectedRevision: current.revision,
            source: { kind: "fixed", id: "default" },
          },
        );
      }
      if (current.kind !== "ready") {
        throw new Error(`Calculator Current recovery reached ${current.kind}.`);
      }
      calculatorState.pendingCalculatorPresetRestore = false;
      calculatorState.calculatorRecoveryRequest = null;
      return current;
    })().catch((error) => {
      showPresetActionError(error, "calculator.recovery.save_failed");
      throw error;
    }).finally(() => {
      calculatorState.calculatorRecoveryPromise = null;
    });
    calculatorState.calculatorRecoveryPromise = operation;
    calculatorState.calculatorPresetEditQueue = operation.catch(() => null);
    return operation;
  }

  function calculatorPresetUrl(signals) {
    return presetURL(signals);
  }

  function calculatorShareText(signals) {
    const current = signals ?? {};
    const projection = calculatorState.projection;
    const presentation = calculatorState.presentation;
    const scenario = projection?.normalizedScenario;
    const summaryText = (metric) => {
      const value = presentation?.summaries?.get?.(metric)?.text;
      if (typeof value !== "string" || value === "") {
        throw new Error(`calculator presentation is missing ${metric}`);
      }
      return value;
    };
    const lead = scenario?.session?.activeFishing
      ? calculatorText("share.active_lead")
      : calculatorText("share.afr_lead", {
          afr: summaryText("autoFishingReduction"),
        });
    const sourceSignals = fishingSourceSignalPatch(scenario?.fishingSource);
    const sourceSelection = sourceSignals.rodZone
      ?? sourceSignals.harpoonZone
      ?? sourceSignals.hotspot;
    return calculatorText("share.link", {
      lead,
      item_drr: summaryText("equipmentDurabilityReduction"),
      zone: String(sourceSelection),
      url: calculatorPresetUrl(current),
    });
  }

  function syncCalculatorActions(signals) {
    const current = signals && typeof signals === "object"
      ? signals
      : signalStore.signalObject();
    if (!current || typeof current !== "object") {
      return;
    }
    calculatorActionTokens.consume(
      current._calculator_actions,
      {
        copyUrlToken: () => {
          window.__fishystuffToast.copyText(calculatorPresetUrl(current), {
            success: calculatorText("toast.preset_url_copied"),
          });
        },
        copyShareToken: () => {
          try {
            window.__fishystuffToast.copyText(calculatorShareText(current), {
              success: calculatorText("toast.share_copied"),
            });
          } catch (error) {
            showPresetActionError(error, "presets.error.save");
          }
        },
      },
    );
  }

  async function restoreCalculator(signals) {
    signalStore.connect(signals);
    const sharedPresetPayload = isPlainObject(sharedCalculatorPresetPayload)
      ? cloneCalculatorSignals(sharedCalculatorPresetPayload)
      : null;
    const storedState = loadStoredSignals({
      includeData: !sharedPresetPayload,
    });
    const storedSignals = storedState.signals;
    if (storedSignals && typeof storedSignals === "object") {
      const restoredSignals = calculatorControlSignalPatch(
        canonicalizeStoredSignals(storedSignals),
      );
      assertKnownPageSignalPatchRoots("calculator", signals, restoredSignals, {
        permittedRoots: CALCULATOR_PRE_INIT_SIGNAL_ROOTS,
      });
      Object.assign(signals, restoredSignals);
    }

    bindCalculatorPresetRuntimes();
    const workspace = presetWorkspace();
    let [calculatorWorkspaceState, layoutWorkspaceState] = await Promise.all([
      workspace.load(CALCULATOR_PRESET_COLLECTION_KEY),
      workspace.load(CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY),
    ]);
    calculatorState.calculatorCurrentStructurallyUnreadable =
      calculatorWorkspaceState.kind === "invalid"
      || calculatorWorkspaceState.kind === "unsupportedVersion";

    if (layoutWorkspaceState.kind === "missing") {
      const layoutPayload = calculatorLayoutPresetPayload(signals._calculator_ui);
      const usesDefault = JSON.stringify(layoutPayload)
        === JSON.stringify(defaultCalculatorLayoutPresetPayload());
      layoutWorkspaceState = await workspace.initialize(
        CALCULATOR_LAYOUT_PRESET_COLLECTION_KEY,
        layoutPayload,
        {
          expectedRevision: layoutWorkspaceState.revision,
          source: usesDefault
            ? { kind: "fixed", id: "default" }
            : { kind: "none", id: "" },
        },
      );
    }

    if (
      calculatorWorkspaceState.kind === "missing"
      && storedState.hasStoredData
      && !sharedPresetPayload
    ) {
      calculatorWorkspaceState = await workspace.initialize(
        CALCULATOR_PRESET_COLLECTION_KEY,
        createVersionedCalculatorPresetPayload(persistedCalculatorSignals(signals)),
        {
          expectedRevision: calculatorWorkspaceState.revision,
          source: { kind: "none", id: "" },
        },
      );
    }

    if (sharedPresetPayload) {
      calculatorState.detachedPresetPreview = true;
      await replaceCalculatorPresetSignals(
        signals,
        createVersionedCalculatorPresetPayload(sharedPresetPayload),
      );
    } else if (calculatorWorkspaceState.kind === "ready") {
      await replaceCalculatorPresetSignals(signals, calculatorWorkspaceState.current.payload);
      calculatorState.calculatorCurrentAppliedBeforeInit = true;
    }

    calculatorState.pendingCalculatorPresetRestore =
      !calculatorPresetDefaultsReady(signals)
      || calculatorWorkspaceState.kind === "unavailable";
    calculatorState.pendingLayoutPresetRestore = false;
    calculatorState.pendingCalculatorDataState = null;
    calculatorState.pendingCalculatorUiState = null;
    if (isPlainObject(signals._calculator_ui)) {
      signals._calculator_ui.initialization_issue =
        calculatorState.calculatorCurrentStructurallyUnreadable
          ? "unreadable-current"
          : "";
      if (calculatorState.calculatorCurrentStructurallyUnreadable) {
        signals._calculator_ui.init_finished = true;
        signals._loading = true;
      }
    }
    const appRoot = document.getElementById?.("calculator");
    if (appRoot && languageHelper()) {
      languageHelper().apply(appRoot);
    }
    bindPetImageFallbackListener();
    calculatorState.uiStateRestored = true;
    calculatorTabs.start();
    bindActionListener();
    bindCalculatorPresetListener();
    bindCalculatorRecoveryListener();
    bindLayoutPresetListener();
    bindCalculatorInteractionListener();
    // Persistence observes completed calculator transitions after preset owners.
    bindPersistListener();
    bindLootRateDisplayPreference();
    calculatorEngineController?.connect();
  }

  function persistCalculator(signals) {
    const persistedData = persistedCalculatorSignals(signals);
    const persistedUi = persistedCalculatorUiSignals(signals);
    try {
      localStorage.setItem(CALCULATOR_DATA_STORAGE_KEY, JSON.stringify(persistedData));
      localStorage.setItem(CALCULATOR_UI_STORAGE_KEY, JSON.stringify(persistedUi));
      return true;
    } catch (error) {
      showPresetActionError(error, "presets.error.save");
      return false;
    }
  }

  function blurActiveElement() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && typeof activeElement.blur === "function") {
      activeElement.blur();
    }
  }

  function syncPackLeaderInputs(slot, checked) {
    const selector = "input[data-pet-pack-leader]";
    const inputs = typeof document.querySelectorAll === "function"
      ? Array.from(document.querySelectorAll(selector))
      : [];
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) {
        continue;
      }
      const inputSlot = Number.parseInt(input.getAttribute("data-pet-pack-leader-slot") || "", 10);
      if (checked) {
        input.checked = inputSlot === slot;
      } else if (inputSlot === slot) {
        input.checked = false;
      }
    }
  }

  function applyPackLeaderChange(input, slot) {
    const normalizedSlot = Number.parseInt(String(slot ?? ""), 10);
    if (!Number.isInteger(normalizedSlot) || normalizedSlot < 1 || normalizedSlot > 5) {
      return;
    }
    const signals = signalStore.signalObject();
    if (!signals) {
      return;
    }
    const targetKey = `pet${normalizedSlot}`;
    const targetPet = signals[targetKey];
    const tierFiveAvailable = targetPet
      && typeof targetPet === "object"
      && !Array.isArray(targetPet)
      && String(targetPet.tier ?? "").trim() === "5";
    const checked = tierFiveAvailable && Boolean(input?.checked);
    syncPackLeaderInputs(normalizedSlot, checked);
    const patch = {};
    for (let index = 1; index <= 5; index += 1) {
      const key = `pet${index}`;
      const pet = signals[key];
      if (!pet || typeof pet !== "object" || Array.isArray(pet)) {
        continue;
      }
      const tierFivePet = String(pet.tier ?? "").trim() === "5";
      const nextValue = tierFivePet
        ? (index === normalizedSlot ? checked : (checked ? false : Boolean(pet.packLeader)))
        : false;
      if (Boolean(pet.packLeader) !== nextValue) {
        patch[key] = { packLeader: nextValue };
      }
    }
    if (Object.keys(patch).length > 0) {
      patchCalculatorSignals(patch);
    }
  }

  async function bootstrapCalculator(signals) {
    await restoreCalculator(signals);
    await languageReady();
    if (calculatorState.calculatorCurrentStructurallyUnreadable) {
      return null;
    }
    return calculatorInitUrl();
  }

  const calculatorBoundary = Object.freeze({
    bootstrap: bootstrapCalculator,
    beginRecovery: beginCalculatorRecovery,
    retryInitialization: retryCalculatorInitialization,
  });
  window.__fishystuffCalculator = calculatorBoundary;
})();
